/**
 * Layout Component Default Properties
 *
 * This file contains the default properties for each layout component type.
 * Used by the page builder and rendering systems.
 */

// Default properties for each component type
export const DEFAULT_COMPONENT_PROPS = {
  row: {
    columns: 2,
    gap: 'md',
    alignment: 'stretch',
    padding: { top: 0, bottom: 0, left: 0, right: 0 },
    margin: { top: 0, bottom: 0, left: 0, right: 0, unit: 'px' },
    /**
     * Direction controls flex layout of columns in the row.
     * Supports responsive breakpoints for mobile, tablet, and desktop.
     * @type {Object|string}
     * @property {string} base - Base direction for mobile ('horizontal' | 'vertical')
     * @property {string|null} tablet - Tablet override (null = inherit from base)
     * @property {string|null} desktop - Desktop override (null = inherit from base)
     *
     * Legacy format: string value ('horizontal' | 'vertical') is also supported
     * and will be auto-converted to responsive format.
     */
    direction: {
      base: 'horizontal', // 'horizontal' or 'vertical'
      tablet: null,  // null = inherit from base
      desktop: null
    },
    backgroundImageUrl: '',
    columnLayout: 'equal', // 'equal', '1-2', '2-1', '1-3', '3-1', '1-1-1', '1-2-1', 'custom'
    columnWidths: [], // Array of widths in 12-column grid (e.g., [4, 8] for 1/3 + 2/3)
    width: {
      base: { value: 'auto', unit: 'px' },
      tablet: null,  // null = inherit from base
      desktop: null
    },
    maxWidth: {
      base: { value: 'auto', unit: 'px' },
      tablet: null,
      desktop: null
    },
    borderColor: 'transparent',
    borderWidth: 0,
    borderStyle: 'solid'
  },
  column: {
    span: 6, // out of 12
    padding: { top: 16, bottom: 16, left: 16, right: 16 },
    margin: { top: 0, bottom: 0, left: 0, right: 0, unit: 'px' },
    backgroundColor: 'transparent',
    borderRadius: 0,
    backgroundImageUrl: '',
    borderColor: 'transparent',
    borderWidth: 0,
    borderStyle: 'solid'
  },
  container: {
    flexDirection: 'column',
    gap: 'md',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    flexWrap: 'nowrap',
    width: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    height: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    minHeight: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    maxWidth: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    padding: { top: 16, bottom: 16, left: 16, right: 16, unit: 'px' },
    margin: { top: 0, bottom: 0, left: 0, right: 0, unit: 'px' },
    backgroundColor: 'transparent',
    opacity: 100,
    borderWidth: 0,
    borderStyle: 'solid',
    borderColor: 'transparent',
    borderRadius: 0,
    overflow: 'visible'
  },
  text: {
    content: 'Enter your text here',
    element: 'p',
    fontSize: 16,
    fontWeight: 'normal',
    color: '#1f2937',
    textAlign: 'left',
    lineHeight: 1.5,
    borderColor: 'transparent',
    borderWidth: 0,
    borderStyle: 'solid',
    borderRadius: 0
  },
  image: {
    src: 'https://placehold.co/600x400?text=New+Image',
    alt: 'Image',
    width: 300,
    height: 200,
    borderRadius: 8,
    objectFit: 'cover',
    borderColor: 'transparent',
    borderWidth: 0,
    borderStyle: 'solid'
  },
  icon: {
    icon: 'Star',
    iconType: 'lucide', // 'lucide', 'emoji', 'custom'
    customIcon: '',
    size: 24,
    width: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    height: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    alignment: 'center',
    color: '#374151',
    style: 'outline', // outline, solid
    borderColor: 'transparent',
    borderWidth: 0,
    borderStyle: 'solid',
    borderRadius: 0
  },
  button: {
    text: 'Button',
    url: '',
    style: 'primary', // primary, secondary, outline
    size: 'md', // sm, md, lg
    backgroundColor: '#3b82f6',
    textColor: '#ffffff',
    borderRadius: 8,
    borderColor: 'transparent',
    borderWidth: 0,
    borderStyle: 'solid'
  },
  link: {
    text: 'Link',
    url: '',
    openInNewTab: false,
    fontFamily: 'System Default',
    fontSize: { base: { value: 16, unit: 'px' }, tablet: null, desktop: null },
    fontWeight: { base: 'normal', tablet: null, desktop: null },
    lineHeight: { base: { value: 1.5, unit: '' }, tablet: null, desktop: null },
    letterSpacing: { base: { value: 0, unit: 'px' }, tablet: null, desktop: null },
    color: '#2563eb',
    textAlign: { base: 'left', tablet: null, desktop: null },
    textDecoration: { base: 'underline', tablet: null, desktop: null },
    borderColor: 'transparent',
    borderWidth: 0,
    borderStyle: 'solid',
    borderRadius: 0,
  },
  spacer: {
    height: 24,
    width: '100%',
    borderColor: 'transparent',
    borderWidth: 0,
    borderStyle: 'solid',
    borderRadius: 0
  },
  listItem: {
    iconUrl: '',
    iconSize: 48,
    text: 'List item text',
    fontSize: 16,
    fontWeight: 'normal',
    lineHeight: 1.5,
    textColor: '#1f2937',
    direction: 'horizontal', // horizontal or vertical
    gap: { value: 16, unit: 'px' },
    paddingTop: 12,
    paddingBottom: 12,
    margin: { top: 0, bottom: 0, left: 0, right: 0, unit: 'px' },
    iconPosition: 'left', // left or top (based on direction)
    borderColor: 'transparent',
    borderWidth: 0,
    borderStyle: 'solid',
    borderRadius: 0
  },
  video: {
    url: '',
    type: 'video/mp4',
    posterUrl: '',
    containerWidth: { value: 100, unit: '%' },
    maxWidth: { value: 'none', unit: 'px' },
    customWidth: 640,
    customHeight: 360,
    aspectRatio: '16:9',
    objectFit: 'contain',
    borderRadius: 8,
    autoplay: false,
    loop: false,
    muted: false,
    controls: true,
    playsinline: true,
    playButton: false,
    preload: 'metadata',
    backgroundColor: '#000000',
    opacity: 100,
    padding: { top: 0, bottom: 0, left: 0, right: 0, unit: 'px' },
    margin: { top: 0, bottom: 0, left: 0, right: 0, unit: 'px' },
    borderColor: 'transparent',
    borderWidth: 0,
    borderStyle: 'solid'
  },
  accordion: {
    items: [
      { id: '1', title: 'Accordion Item 1', content: 'Content for the first accordion item goes here.', defaultOpen: true },
      { id: '2', title: 'Accordion Item 2', content: 'Content for the second accordion item goes here.', defaultOpen: false }
    ],
    allowMultiple: false,
    iconPosition: 'right',
    iconType: 'chevron',
    headerBackgroundColor: '#f9fafb',
    headerTextColor: '#1f2937',
    headerFontFamily: 'System Default',
    headerFontSize: 16,
    headerFontWeight: '600',
    headerPadding: { base: { top: 16, bottom: 16, left: 16, right: 16, unit: 'px' }, tablet: null, desktop: null },
    contentBackgroundColor: '#ffffff',
    contentTextColor: '#4b5563',
    contentFontFamily: 'System Default',
    contentFontSize: 14,
    contentPadding: { base: { top: 16, bottom: 16, left: 16, right: 16, unit: 'px' }, tablet: null, desktop: null },
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#e5e7eb',
    borderRadius: 8,
    itemGap: 0,
    margin: { base: { top: 0, bottom: 0, left: 0, right: 0, unit: 'px' }, tablet: null, desktop: null },
    visibility: { base: true, tablet: null, desktop: null },
    customCSS: ''
  }
};
